import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";
import { dissoc as Rdissoc } from "ramda";

import { TOAST_OPTIONS } from "@/constants";
import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import {
  buildFormUser,
  prettifyUserStatus,
  prettifyUserPermissionLevel,
} from "@/models/User";
import { User } from "@/store/modules/users";

export default defineComponent({
  name: "userEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);
    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteUser = () => {
      deleting.value = true;
      const jsConn = new JSConn();
      jsConn.toRoutePath = "/users";
      jsConn.formParams = {
        id: props.userId,
      };
      $store.dispatch("deleteUser", jsConn).then(jsConn => {
        deleting.value = false;
        ctx.root.$router.push(jsConn.toRoutePath);
        return Promise.resolve(jsConn);
      });
    };

    const cancelParams = () => {
      if (props.userId === "new") {
        return {
          name: NamedRts.users,
        };
      } else {
        return {
          name: NamedRts.users,
        };
      }
    };

    let {
      loadUserIntoForm,
      formUser,
      userStatuses,
      loadUserStatusesIntoForm,
      userPermissionLevels,
      loadUserPermissionLevelsIntoForm,
    } = buildFormUser();

    const apiUser = computed(() => {
      return $store.getters.user as User;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();
      loadUserStatusesIntoForm(userStatuses);
      loadUserPermissionLevelsIntoForm(userPermissionLevels);

      if (props.userId !== "new") {
        jsConn.formParams = {
          id: props.userId,
        };

        dataPromises.push($store.dispatch("needUser", jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.userId !== "new") {
            loadUserIntoForm(apiUser.value, formUser);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();
      let formParams;

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      if (props.userId === "new") {
        formUser.id = uuidv4();
      }

      // remove the password if it is an empty string
      // if something gets changed and the password may be null, the check will
      // need to be changed also.
      // this allows for the admin changing things without changing the password
      if (formUser.password === "") {
        formParams = Rdissoc("password", formUser);
      } else {
        formParams = formUser;
      }

      (jsConn.formParams = formParams), (jsConn.toRoutePath = "/users");

      let storeDispatch;

      if (props.userId === "new") {
        storeDispatch = "createUser";
      } else {
        storeDispatch = "updateUser";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(jsConn => {
          ctx.root.$bvToast.toast(
            "Client was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(jsConn.toRoutePath);
          return Promise.resolve(jsConn);
        })
        .catch(jsConn => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      cancelParams,

      formUser,

      onSubmit,
      submitButtonText,
      submitting,
      hasFormErrors,
      formErrors,

      userStatuses,
      userPermissionLevels,
      prettifyUserStatus,
      prettifyUserPermissionLevel,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleting,
      deleteUser,
    };
  },
});
